import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { LandingDialogComponent } from "../landing.dialog/landing.dialog.component";
import { AppStateService } from "../shared/service/app.state.service";
import { OidcService } from "../core/oidc/oidc.service";
import { take } from "rxjs/operators";
import { AngularAppLogger } from '../angular-app-logger';
import { ConfirmationDialogComponent } from '../confirm.dialog/confirmation.dialog.component';

@Component({
  selector: 'app-landing.page',
  templateUrl: './landing.page.component.html',
  styleUrls: ['./landing.page.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class LandingPageComponent implements OnInit {
  isDialogShown = false;

  constructor(
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly appState: AppStateService,
    private readonly oidcService: OidcService,
    private readonly appLogger: AngularAppLogger,
  ) {}

  ngOnInit() {
    console.log('LandingPageComponent ngOnInit called', window.location.href);

    // Handle logout state separately
    if (window.history?.state?.fromLogout) {
      history.replaceState({}, '', window.location.pathname);
      return;
    }

    // At this point, AuthGuard has already ensured authentication
    this.oidcService.getAccessToken().pipe(take(1)).subscribe(token => {
      if (token) {
        this.processOidcToken(token);
      } else {
        // Safety net: shouldn't normally happen
        console.error('AuthGuard passed but no token found. Forcing login.');
        this.oidcService.login();
      }
    });

    // Ensure patient is saved in appState and removed from localStorage rightaway - we do not need to keep it in localStorage anymore
    const bufferedPatient = sessionStorage.getItem("smart_patient_buffer");
    if (bufferedPatient) {
      this.appState.setPatient(bufferedPatient);
      // localStorage.removeItem("smart_patient_buffer");
    }
  }

  private processOidcToken(accessToken: string) {
    try {
      const decodedValue = this.oidcService.decodeToken(accessToken);
      this.processTokenClaims(decodedValue, accessToken);
    } catch (error) {
      console.error('Error decoding OIDC token:', error);
    }
  }

  private processTokenClaims(decodedValue: any, accessToken: string) {
    if (decodedValue.authz_msync_fhir_ehr_client && decodedValue.authz_cibmtr_fhir_ehr_client) {
      this.showCustomDialog(decodedValue, accessToken);
    } else if (decodedValue.authz_cibmtr_fhir_ehr_client) {
      this.appState.updateAppLabel("cra");
      this.appState.setBackButtonState(false);
      this.appLogger.setUserContext("cra", decodedValue);
      this.router.navigate(["./patient-component"], { state: { oktaToken: accessToken, decoded: decodedValue } });
    } else if (decodedValue.authz_msync_fhir_ehr_client) {
      this.appState.updateAppLabel("msync");
      this.appState.setBackButtonState(false);
      this.appLogger.setUserContext("msync", decodedValue);
      this.router.navigate(["./patient-import"], { state: { oktaToken: accessToken, decoded: decodedValue } });
    }
  }

  private confirmLogout(decodedValue: any, accessToken: string): void {
    const logoutDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '490px',
      panelClass: "custom-dialog-container",
      disableClose: false,
      hasBackdrop: true,
      data: {
        confirmMessage: 'Are you sure? Logout will remove the previously accessed Patient data.'
      }
    });

    logoutDialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.oidcService.logout();
      } else {
        this.showCustomDialog(decodedValue, accessToken);
      }
    });
  }

  showCustomDialog(decodedValue: any, accessToken: string): void {
    if (this.isDialogShown) return;
    this.isDialogShown = true;

    this.appState.updateAppLabel(undefined);
    this.appState.setBackButtonState(true);

    const dialogRef = this.dialog.open(LandingDialogComponent, {
      width: '1200px',
      height: '550px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      this.isDialogShown = false;

      if (result === 'msync') {
        this.appState.updateAppLabel("msync");
        this.appLogger.setUserContext("msync", decodedValue);
        this.router.navigate(["./patient-import"], { state: { oktaToken: accessToken, decoded: decodedValue } });
      } else if (result === 'cra') {
        this.appState.updateAppLabel("cra");
        this.appLogger.setUserContext("cra", decodedValue);
        this.router.navigate(["./patient-component"], { state: { oktaToken: accessToken, decoded: decodedValue } });
      } else if (result === 'logout') {
        this.confirmLogout(decodedValue, accessToken);
      }
      // If result is undefined/null (dialog cancelled), do nothing - stay on current page
    });
  }


}
